import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Dialog, DialogContent, DialogTitle, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, Paper, TextField, Select, MenuItem, FormControl, InputLabel, List,
  ListItem, ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { collection, getDocs, query, where, onSnapshot, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import dayjs from 'dayjs';
import {
  ShoppingCart as CartIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  People as UsersIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import QRCode from "qrcode";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const DashboardCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  transition: 'transform 0.3s ease-in-out',
  boxShadow: theme.shadows[3],
  borderRadius: 12,
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
  },
}));

const ChartCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  width: '100%',
  maxWidth: 500,
  margin: '0 auto',
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 300,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    height: 250,
  },
}));

const Home = ({ companyType, companyCode: propCompanyCode, companyName, isPureVeg, userId, unitCode: propUnitCode }) => {
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    activeUsers: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [tableData, setTableData] = useState({ occupied: 0, unoccupied: 0 });
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAllOrders, setShowAllOrders] = useState(false);
  const today = dayjs().format('YYYY-MM-DD');
const [billingPreference, setBillingPreference] = useState(null);
const [showCategorySeparate, setShowCategorySeparate] = useState(null);
const [startDate, setStartDate] = useState(today);
const [endDate, setEndDate] = useState(today);
  const [statusFilter, setStatusFilter] = useState('All');
  const [companyCode, setCompanyCode] = useState(propCompanyCode);
  const [unitCode, setUnitCode] = useState(propUnitCode);
  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState(null);
  const [monthOrdersCount, setMonthOrdersCount] = useState(0);
  const [todayOrdersCount, setTodayOrdersCount] = useState(0);
  const [yearRevenue, setYearRevenue] = useState(0);
  const [monthRevenue, setMonthRevenue] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);

  const fetchCompanyDetails = async (code) => {
  try {
    const docRef = doc(db, "companies", code);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      setCompanyDetails(docSnap.data());
    }
  } catch (err) {
    console.error("Error fetching company details:", err);
  }
};
  useEffect(() => {
    const initialize = async () => {
      let resolvedUnitCode = unitCode;
      let resolvedCompanyCode = companyCode;

      // Extract unitCode from userId if not provided
      if (!resolvedUnitCode && userId) {
        const parts = userId.split('#');
        if (parts.length === 2 && parts[0]) {
          resolvedUnitCode = parts[0];
        }
      }

      // Validate unitCode and fetch companyCode
      if (resolvedUnitCode) {
        try {
          const unitDocRef = doc(db, 'units', resolvedUnitCode);
          const unitDoc = await getDoc(unitDocRef);
          if (unitDoc.exists()) {
            const unitData = unitDoc.data();
            resolvedCompanyCode = unitData.companyCode;
            setUnitCode(resolvedUnitCode);
            setCompanyCode(resolvedCompanyCode);
            await fetchCompanyDetails(resolvedCompanyCode);
          } else {
            setError('Invalid unit code.');
            return;
          }
        } catch (error) {
          console.error('Error validating unit:', error);
          setError('Failed to validate unit code.');
          return;
        }
      } else if (!resolvedCompanyCode) {
        setError('Company or unit code is missing.');
        return;
      }

      // Orders query
      const ordersQuery = query(
        collection(db, 'orders'),
        where(unitCode ? 'unitCode' : 'companyCode', '==', unitCode || resolvedCompanyCode)
      );

      const unsubscribeOrders = onSnapshot(ordersQuery, async (snapshot) => {
        try {
          const orders = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => {
              const aTime = a.createdAt instanceof Timestamp ? a.createdAt.toDate() : new Date(a.createdAt || 0);
              const bTime = b.createdAt instanceof Timestamp ? b.createdAt.toDate() : new Date(b.createdAt || 0);
              return bTime - aTime; // Newest first
            });

          setAllOrders(orders);

          // Apply filters
          let filteredOrders = orders;
          if (startDate) {
            const start = dayjs(startDate).startOf('day').toDate();
            filteredOrders = filteredOrders.filter(order =>
              order.createdAt instanceof Timestamp &&
              order.createdAt.toDate() >= start
            );
          }
          if (endDate) {
            const end = dayjs(endDate).endOf('day').toDate();
            filteredOrders = filteredOrders.filter(order =>
              order.createdAt instanceof Timestamp &&
              order.createdAt.toDate() <= end
            );
          }
          /*if (statusFilter !== 'All') {
            filteredOrders = filteredOrders.filter(order => (order.status || 'Confirmed') === statusFilter);
          }*/

          if (statusFilter !== 'All') {
  filteredOrders = filteredOrders.filter(order => {
    return (order.orderStatus || 'Open') === statusFilter;
  });
}

          setRecentOrders(showAllOrders ? filteredOrders : filteredOrders.slice(0, 5));

          //const totalOrders = orders.length;

          // Year Till Date Orders
          const startOfYear = dayjs().startOf('year');
          const endOfYear = dayjs().endOf('day');

          const yearOrders = orders.filter(order => {
            if (!(order.createdAt instanceof Timestamp)) return false;

            const orderDate = dayjs(order.createdAt.toDate());

            return (
              orderDate.isAfter(startOfYear.subtract(1, 'millisecond')) &&
              orderDate.isBefore(endOfYear.add(1, 'millisecond'))
            );
          });

          const totalOrders = yearOrders.length;

          // Current month start and end
          const startOfMonth = dayjs().startOf('month');
          const endOfMonth = dayjs().endOf('month');

          // Month-to-date orders
          const monthOrders = orders.filter(order => {
            if (!(order.createdAt instanceof Timestamp)) return false;

            const orderDate = dayjs(order.createdAt.toDate());

            return (
              orderDate.isAfter(startOfMonth.subtract(1, 'millisecond')) &&
              orderDate.isBefore(endOfMonth.add(1, 'millisecond'))
            );
          });

          setMonthOrdersCount(monthOrders.length);

          // Today's orders
          const startOfToday = dayjs().startOf('day');
          const endOfToday = dayjs().endOf('day');

          const todayOrders = orders.filter(order => {
            if (!(order.createdAt instanceof Timestamp)) return false;

            const orderDate = dayjs(order.createdAt.toDate());

            return (
              orderDate.isAfter(startOfToday.subtract(1, 'millisecond')) &&
              orderDate.isBefore(endOfToday.add(1, 'millisecond'))
            );
          });

          setTodayOrdersCount(todayOrders.length);

          //const totalRevenue = orders.reduce((sum, order) => sum + (Number(order.totalAmount) || 0), 0);
          const totalRevenue = orders.reduce(
            (sum, order) => sum + (Number(order.grandTotal) || 0),
            0
          );
          //year total revenue
          const yearRevenueValue = yearOrders.reduce(
            (sum, order) => sum + (Number(order.grandTotal) || 0), 0);

          setYearRevenue(yearRevenueValue);

          //month total revenue
          const monthRevenueValue = monthOrders.reduce(
            (sum, order) => sum + (Number(order.grandTotal) || 0),0);

          setMonthRevenue(monthRevenueValue);

          //today total revenue
          const todayRevenueValue = todayOrders.reduce(
            (sum, order) => sum + (Number(order.grandTotal) || 0),
            0
          );

          setTodayRevenue(todayRevenueValue);

          // Fetch users
          const fetchUsers = async () => {
            try {
              const usersQuery = query(
                collection(db, 'users'),
                where(unitCode ? 'unitCode' : 'companyCode', '==', unitCode || resolvedCompanyCode)
              );
              const usersSnapshot = await getDocs(usersQuery);
              const activeUsers = usersSnapshot.docs.length;
              setMetrics({ totalOrders, totalRevenue, activeUsers });
            } catch (error) {
              console.error('Error fetching users:', error);
              setError('Failed to load user data.');
            }
          };
          fetchUsers();

          // Status chart data
          const statusCounts = orders.reduce((acc, order) => {
            const status = order.status || 'Confirmed';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {});
          const statusChartData = Object.keys(statusCounts).map((status, index) => ({
            id: index,
            value: statusCounts[status],
            label: status,
          }));
          setStatusData(statusChartData);

          // Revenue chart data
          const last30Days = Array.from({ length: 30 }, (_, i) =>
            dayjs().subtract(i, 'day').format('YYYY-MM-DD')
          ).reverse();
          const revenueByDay = orders.reduce((acc, order) => {
            if (order.createdAt && order.createdAt.toDate) {
              const date = dayjs(order.createdAt.toDate()).format('YYYY-MM-DD');
              if (last30Days.includes(date)) {
                acc[date] = (acc[date] || 0) + (Number(order.totalAmount) || 0);
              }
            }
            return acc;
          }, {});
          const revenueChartData = last30Days.map(date => ({
            date,
            revenue: revenueByDay[date] || 0,
          }));
          setRevenueData(revenueChartData);

          console.log('Home: Fetched order data:', {
            totalOrders,
            totalRevenue,
            recentOrders: filteredOrders.slice(0, 5),
            statusChartData,
            revenueChartData,
            unitCode: resolvedUnitCode,
            companyCode: resolvedCompanyCode,
          });
        } catch (error) {
          console.error('Error processing orders snapshot:', error);
          setError('Failed to load dashboard data.');
        }
      }, (error) => {
        console.error('Orders snapshot listener error:', error);
        setError('Failed to listen for order updates.');
      });

      // Tables query
      const tablesQuery = query(
        collection(db, 'tables'),
        where(unitCode ? 'unitCode' : 'companyCode', '==', unitCode || resolvedCompanyCode)
      );

      const unsubscribeTables = onSnapshot(tablesQuery, (snapshot) => {
        try {
          const tables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const occupied = tables.filter(table => table.occupied).length;
          const unoccupied = tables.length - occupied;
          setTableData({ occupied, unoccupied });
          console.log('Home: Fetched table data:', { occupied, unoccupied, unitCode: resolvedUnitCode });
        } catch (error) {
          console.error('Error processing tables snapshot:', error);
          setError('Failed to load table data.');
        }
      }, (error) => {
        console.error('Tables snapshot listener error:', error);
        setError('Failed to listen for table updates.');
      });

      return () => {
        console.log('Home: Cleaning up listeners');
        unsubscribeOrders();
        unsubscribeTables();
      };
    };

    initialize();
  }, [companyCode, unitCode, userId, startDate, endDate, statusFilter, showAllOrders]);
const [categoryPreference, setCategoryPreference] = useState(null);

useEffect(() => {

  const fetchCategoryPreference = async () => {

    if (!companyCode || !unitCode) return;

    try {

      const q = query(
        collection(db, "preferences"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", unitCode),
        where("preferenceName", "==", "Category To Be Shown Separate")
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {

        const prefData = snapshot.docs[0].data();

        setShowCategorySeparate(
  prefData.preferenceValue !== null &&
  prefData.preferenceValue !== undefined &&
  prefData.preferenceValue !== ""
    ? Number(prefData.preferenceValue)
    : null
);


        setCategoryPreference(prefData); 
      }

    } catch (err) {

      console.error(
        "Failed to fetch category preference:",
        err
      );

    }
  };

  fetchCategoryPreference();

}, [companyCode, unitCode]);

  const handleOrderClick = async (order) => {
    try {
      const paymentQuery = query(
        collection(db, 'payments'),
        where('orderId', '==', order.id),
        where(unitCode ? 'unitCode' : 'companyCode', '==', unitCode || companyCode)
      );
      const paymentSnapshot = await getDocs(paymentQuery);
      const payment = paymentSnapshot.docs.length > 0 ? paymentSnapshot.docs[0].data() : null;
      setSelectedOrder({
        ...order,
        paymentStatus: payment && payment.status === 'Success' ? 'Paid' : 'Unpaid',
       // paymentMethod: payment?.paymentMethod || 'N/A',
        paymentMethod:
        payment?.paymentMethod ||   // from payments collection
        order?.paymentMethod ||     // fallback from orders
        'N/A',
      });
    } catch (error) {
      console.error('Error fetching payment for order:', error);
      setSelectedOrder({
        ...order,
        paymentStatus: 'Unknown',
        //paymentMethod: 'N/A',
        paymentMethod: order?.paymentMethod || 'N/A',
      });
    }
  };

  const handleCloseDialog = () => {
    setSelectedOrder(null);
  };

  const generateUpiQr = async (unitCode, amount) => {
    try {
      const unitRef = doc(db, "units", unitCode);
      const unitSnap = await getDoc(unitRef);
      if (!unitSnap.exists()) return "";
  
      const unitData = unitSnap.data();
      const unitUpiDetails = unitData.upiDetails;
      if (!unitUpiDetails || unitUpiDetails.length === 0) return "";
  
      const upi = unitUpiDetails[0];
  
      const upiURL = `upi://pay?pa=${encodeURIComponent(upi.upiAddress)}&pn=${encodeURIComponent(upi.payeeName)}&tn=${encodeURIComponent(upi.description || "")}&am=${amount?.toFixed(2) || 0}&cu=INR`;
  
      const qrDataURL = await QRCode.toDataURL(upiURL, {
        errorCorrectionLevel: "H",
        type: "image/png",
        margin: 2,
        width: 150,
      });
  
      return qrDataURL;
    } catch (err) {
      console.error("Failed to generate QR code:", err);
      return "";
    }
  };

  

  const handlePrintBill = async(order) => {
   const iframe = document.createElement("iframe");

iframe.style.position = "fixed";
iframe.style.right = "0";
iframe.style.bottom = "0";
iframe.style.width = "0";
iframe.style.height = "0";
iframe.style.border = "0";

document.body.appendChild(iframe);

const printWindow = iframe.contentWindow;

printWindow.document.open();
printWindow.document.write(`
  <!-- your entire HTML here -->
`);
printWindow.document.close();

printWindow.onload = () => {
  printWindow.focus();
  printWindow.print();

  printWindow.onafterprint = () => {
    document.body.removeChild(iframe);
  };
};

    const unitRef = doc(db, "units", order.unitCode);
const unitSnap = await getDoc(unitRef);

let gstin = "";
let city = "";

if (unitSnap.exists()) {
  const unitData = unitSnap.data();
  gstin = unitData.gstin || "";
  city = unitData.city || "";
}


    const companyRef = doc(db, "companies", order.companyCode);
const companySnap = await getDoc(companyRef);

let companyPhone = "";

if (companySnap.exists()) {
  const companyData = companySnap.data();
  companyPhone = companyData.phone || "";
}
    const qr = await generateUpiQr(order.unitCode, order.grandTotal);

   const shouldSeparateCategory =
  showCategorySeparate !== null &&
  showCategorySeparate !== undefined &&
  showCategorySeparate !== "";

    const selectedCategory =
  categoryPreference?.preferenceOptions?.[
    showCategorySeparate
  ]
    ?.trim()
    ?.toLowerCase() || "";

const getItemCategory = (aggregatedItem) => {
  return String(aggregatedItem.categoryName || "")
    .trim()
    .toLowerCase();
};


const separateCategoryItems = shouldSeparateCategory
  ? order.items.filter(
      item => getItemCategory(item) === selectedCategory
    )
  : [];

const normalItems = shouldSeparateCategory
  ? order.items.filter(
      item => getItemCategory(item) !== selectedCategory
    )
  : order.items;

const renderRows = (items) =>
  items.map(
    item => `
      <tr>
        <td class="item-name">${item.name || 'N/A'}</td>
        <td class="number">${item.quantity || 1}</td>
        <td class="number">₹${item.price?.toFixed(2) || '0.00'}</td>
        <td class="number">₹${item.totalPrice?.toFixed(2) || '0.00'}</td>
      </tr>
    `
  ).join("");

let rows = "";

if (!shouldSeparateCategory) {

  rows = renderRows(order.items);

} else {

  rows = `

    ${renderRows(normalItems)}

    ${
      separateCategoryItems.length > 0
        ? `
          <tr>
            <td colspan="4" style="height:20px; border:none;"></td>
          </tr>

          <tr>
            <td colspan="4"
                style="
                  font-weight:bold;
                  text-align:left;
                  background:#f0f0f0;
                  padding:10px;
                ">
               
          ${
  categoryPreference?.preferenceOptions?.[
    showCategorySeparate
  ] || ""
}
            </td>
          </tr>

          ${renderRows(separateCategoryItems)}
        `
        : ""
    }

  `;
}

// Calculate totals for cancelled orders
const cancelledItemsTotal = (order.items || []).reduce(
  (sum, item) => sum + ((item.totalPrice || 0)),
  0
);

const displayBaseSubtotal =
  order.orderStatus === "Cancelled"
    ? cancelledItemsTotal
    : (order.baseSubtotal || 0);

const displayGrandTotal =
  order.orderStatus === "Cancelled"
    ? cancelledItemsTotal
    : (order.grandTotal || 0);

    printWindow.document.write(`
      <html>
        <head>
          <title>Bill - Order ${order.id}</title>
         <style>
 body {
  font-family: Arial, sans-serif;
  padding: 0;
  margin: 0;
  font-size: 14px;   /* BIGGER COMMON FONT */
  color: #000;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
 margin:0;
}

th, td {
  padding: 2px 0;
  font-size: 14px;   /* SAME SIZE */
  border: none;
}

thead tr {
  border-top: 1px dashed #000;
  border-bottom: 1px dashed #000;
}

tbody tr:last-child {
  border-bottom: 1px dashed #000;
}

.item-name {
  width: 52%;
  text-align: left;
  padding-left: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.number {
  width: 16%;
  text-align: right;
  white-space: nowrap;
  padding-right: 2px;
}

.bill-header {
  text-align: center;
  margin-bottom: 10px;
}

.bill-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
}

.bill-header div {
  margin-top: 3px;
  font-size: 14px;   /* SAME FONT SIZE */
}

.total {
  padding: 0 ;
  text-align: right;
  font-size: 14px;
}

.grand-total {
  font-weight: bold;
  font-size: 16px;
  margin: 4px 0;
}

.footer-message {
  text-align: center;
  margin-top: 10px;
  font-size: 14px;
}

.qr {
  text-align: center;
}

.qr img {
  width: 120px;
  margin-top: 8px;
}
@media print and (max-width: 80mm) {

  body {
    width: 80mm;
    padding: 0;
    margin: 0;
    font-family: Arial, sans-serif;
    font-size: 11px;
    line-height: 1.2;
  }

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
   margin: 0;
  }

  th,
  td {
    padding: 1px 0;
    font-size: 11px;
  }

  .item-name {
    width: 52%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .number {
    width: 16%;
    text-align: right;
    white-space: nowrap;
  }

  .bill-header {
    text-align: center;
    margin-bottom: 5px;
  }

  .bill-header h2 {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
  }

  .bill-header div {
    margin-top: 1px;
    font-size: 11px;
  }

  thead tr {
    border-top: 1px dashed #000;
    border-bottom: 1px dashed #000;
  }

  tbody tr:last-child {
    border-bottom: 1px dashed #000;
  }

  .total {
   padding:0;
    text-align: right;
    font-size: 11px;
  }

  .grand-total {
    font-size: 13px;
    font-weight: bold;
    margin: 3px 0;
  }

  .footer-message {
    text-align: center;
    margin-top: 6px;
    font-size: 11px;
  }

  .qr {
    text-align: center;
  }

  .qr img {
    width: 90px;
    margin-top: 6px;
  }

  @page {
    size: 80mm auto;
     margin: 0 !important;
  }
}

</style>
        </head>
        <body>
         <div class="bill-header">
            <h2 style="margin:0;">Bill - ${companyName || 'Unknown Company'}</h2>
            ${
  city || companyPhone
    ? `
      <div style="
        font-size:15px;
        display:flex;
        justify-content:center;
        gap:5px;
        flex-wrap:wrap;
      ">
        ${city ? `<span>${city}</span>` : ""}
        ${companyPhone ? `<span>Ph: ${companyPhone}</span>` : ""}
      </div>
    `
    : ""
}
             ${gstin ? `<div><b>GSTIN:</b> ${gstin}</div>` : ""}
            <div><b>Bill No:</b> ${order.billNo ?? 'N/A'}</div>
${
  order.tableId || order.managerId
    ? `
      <div style="
        display:flex;
        justify-content:center;
        gap:20px;
        margin-top:3px;
      ">
        ${
          order.tableId
            ? `<span><b>Table:</b> ${order.tableId}</span>`
            : ""
        }

        ${
          order.managerId
            ? `<span><b>Waiter:</b> ${
                order.managerId
              }</span>`
            : ""
        }
      </div>
    `
    : ""
}
           <div><b>Date:</b> ${
  order.createdAt?.toDate()
    ? order.createdAt.toDate().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : 'N/A'
}</div>
             <div><b>Payment Method:</b> ${order.paymentMethod || 'N/A'}</div>
          </div>
          <table>
           <thead>
            <tr>
              <th class="item-name">Item</th>
              <th class="number">Quantity</th>
              <th class="number">Price (₹)</th>
              <th class="number">Total (₹)</th>
            </tr>
             </thead>
             <tbody>${rows}</tbody>
          </table>
       <div class="total">

  ${(order.cgstTotal || 0) > 0 ? `
    <div>Total CGST: ₹${(order.cgstTotal || 0).toFixed(2)}</div>
  ` : ''}

  ${(order.sgstTotal || 0) > 0 ? `
    <div>Total SGST: ₹${(order.sgstTotal || 0).toFixed(2)}</div>
  ` : ''}

  <h2 style="padding: 0; margin: 2px 0;">
    Grand Total: ₹${displayGrandTotal.toFixed(2)}
  </h2>

</div>
         ${order.paymentStatus === 'Paid' ? `
  <p>Payment Status: Paid</p>
` : ''}
          ${qr ? `
  <div style="text-align:center; margin-top:20px;">
    <p><strong>Scan & Pay</strong></p>
    <img src="${qr}" width="150" />
  </div>
` : ""}
         <div class="footer-message">
  Thank you for dining with us. Visit again!
</div>
           <script>
window.onload = () => {
  window.print();

  window.onafterprint = () => {
    window.close();
  };
};
</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

 /* const handleViewAllOrders = () => {
    const queryParams = new URLSearchParams({
      code: companyCode || '',
      unit: unitCode || '',
      type: companyType || 'Unknown',
      name: companyName || 'Unknown',
      isPureVeg: (isPureVeg || false).toString(),
    });
    navigate(`/unit_manager/orders?${queryParams.toString()}`);
  };*/
  const handleViewAllOrders = (filterType = "year") => {
    const queryParams = new URLSearchParams({
      code: companyCode || '',
      unit: unitCode || '',
      type: companyType || 'Unknown',
      name: companyName || 'Unknown',
      isPureVeg: (isPureVeg || false).toString(),
      filterType,
    });

    navigate(`/unit_manager/orders?${queryParams.toString()}`);
  };

  /*const handlePayments = () => {
    const queryParams = new URLSearchParams({
      code: companyCode || '',
      unit: unitCode || '',
      type: companyType || 'Unknown',
      name: companyName || 'Unknown',
      isPureVeg: (isPureVeg || false).toString(),
    });
    navigate(`/unit_manager/payments?${queryParams.toString()}`);
  };*/

  const handlePayments = (filterType = "year") => {
    const queryParams = new URLSearchParams({
      code: companyCode || '',
      unit: unitCode || '',
      type: companyType || 'Unknown',
      name: companyName || 'Unknown',
      isPureVeg: (isPureVeg || false).toString(),
      filterType,
    });

    navigate(`/unit_manager/payments?${queryParams.toString()}`);
  };

  const handleManageMenu = () => {
    const queryParams = new URLSearchParams({
      code: companyCode || '',
      unit: unitCode || '',
      type: companyType || 'Unknown',
      name: companyName || 'Unknown',
      isPureVeg: (isPureVeg || false).toString(),
    });
    navigate(`/unit_manager/menu?${queryParams.toString()}`);
  };

  const handleManageUsers = () => {
    const queryParams = new URLSearchParams({
      code: companyCode || '',
      unit: unitCode || '',
      type: companyType || 'Unknown',
      name: companyName || 'Unknown',
      isPureVeg: (isPureVeg || false).toString(),
    });
    navigate(`/unit_manager/add-user?${queryParams.toString()}`);
  };

  const pieChartData = {
    labels: statusData.map(item => item.label),
    datasets: [
      {
        data: statusData.map(item => item.value),
        backgroundColor: ['#1976d2', '#ef5350', '#4caf50', '#ff9800'],
        borderColor: '#fff',
        borderWidth: 1,
      },
    ],
  };

  const barChartData = {
    labels: revenueData.map(item => dayjs(item.date).format('MMM D')),
    datasets: [
      {
        label: 'Revenue (₹)',
        data: revenueData.map(item => item.revenue),
        backgroundColor: '#1976d2',
        borderColor: '#1976d2',
        borderWidth: 1,
      },
    ],
  };

  const pieTableData = {
    labels: ['Occupied', 'Unoccupied'],
    datasets: [
      {
        data: [tableData.occupied, tableData.unoccupied],
        backgroundColor: ['#4caf50', '#ef5350'],
        borderColor: '#fff',
        borderWidth: 1,
      },
    ],
  };

  const totalGrandTotal = recentOrders.reduce(
  (sum, order) => sum + (Number(order.grandTotal) || 0),
  0
);
  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4, textAlign: 'center' }}
      >
        Unit Manager Dashboard - {companyName || 'Unknown Company'}
      </Typography>
      {error && (
        <Typography color="error" sx={{ textAlign: 'center', mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={4} sx={{ mb: 4, justifyContent: 'space-between' }}>
        <Grid item xs={12} sm={6} md={4} width={300} sx={{ ml: 5 }}>
          <DashboardCard sx={{ bgcolor: '#e3f2fd', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CartIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Orders (Year)
                </Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {metrics.totalOrders}
              </Typography>
            </CardContent>
            <Button
              size="small"
              onClick={() => handleViewAllOrders("year")}
              sx={{ m: 2, color: 'primary.main', fontWeight: 'medium' }}
            >
              View Orders
            </Button>
          </DashboardCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4} width={300}>
          <DashboardCard sx={{ bgcolor: '#fff3e0', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CartIcon sx={{ color: '#ef6c00', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Orders (Month)
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#ef6c00' }}>
                {monthOrdersCount}
              </Typography>
            </CardContent>
            <Button
              size="small"
              onClick={() => handleViewAllOrders("month")}
              sx={{ m: 2, color: '#ef6c00', fontWeight: 'medium' }}
            >
              View Orders
            </Button>
          </DashboardCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4} width={300}>
          <DashboardCard sx={{ bgcolor: '#f3e5f5', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CartIcon sx={{ color: '#8e24aa', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Orders (Today)
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#8e24aa' }}>
                {todayOrdersCount}
              </Typography>
            </CardContent>
            <Button
              size="small"
              onClick={() => handleViewAllOrders("today")}
              sx={{ m: 2, color: '#8e24aa', fontWeight: 'medium' }}
            >
              View Orders
            </Button>
          </DashboardCard>
        </Grid>
        {/*<Grid item xs={12} sm={6} md={4} width={300} sx={{ mr: 5 }}>
          <DashboardCard sx={{ bgcolor: '#e0f7fa', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <UsersIcon sx={{ color: '#0288d1', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Employees
                </Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {metrics.activeUsers}
              </Typography>
            </CardContent>
            <Button
              size="small"
              onClick={handleManageUsers}
              sx={{ m: 2, color: 'primary.main', fontWeight: 'medium' }}
            >
              Manage Employees
            </Button>
          </DashboardCard>
        </Grid>*/}
      </Grid>

      {/*<Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={4} width={300}>
          <DashboardCard sx={{ bgcolor: '#e8f5e9', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ color: '#388e3c', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Revenue
                </Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                ₹{metrics.totalRevenue.toFixed(2)}
              </Typography>
            </CardContent>
            <Button
              size="small"
              onClick={handlePayments}
              sx={{ m: 2, color: 'primary.main', fontWeight: 'medium' }}
            >
              View Details
            </Button>
          </DashboardCard>
        </Grid>
      </Grid>*/}

      <Grid container spacing={12} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={4} width={300} sx={{ ml: 5 }}>
          <DashboardCard sx={{ bgcolor: '#e8f5e9', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ color: '#2e7d32', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Revenue (Year)
                </Typography>
              </Box>

              <Typography variant="h4" sx={{ color: '#2e7d32' }}>
                ₹{yearRevenue.toFixed(2)}
              </Typography>
            </CardContent>

            <Button
              size="small"
              onClick={() => handlePayments("year")}
              sx={{ m: 2, color: '#2e7d32', fontWeight: 'medium' }}
            >
              View Details
            </Button>
          </DashboardCard>
        </Grid>

        <Grid item xs={12} sm={6} md={4} width={300}>
          <DashboardCard sx={{ bgcolor: '#fff8e1', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ color: '#f9a825', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Revenue (Month)
                </Typography>
              </Box>

              <Typography variant="h4" sx={{ color: '#f9a825' }}>
                ₹{monthRevenue.toFixed(2)}
              </Typography>
            </CardContent>

            <Button
              size="small"
              onClick={() => handlePayments("month")}
              sx={{ m: 2, color: '#f9a825', fontWeight: 'medium' }}
            >
              View Details
            </Button>
          </DashboardCard>
        </Grid>

        <Grid item xs={12} sm={6} md={4} width={300}>
          <DashboardCard sx={{ bgcolor: '#f3e5f5', maxWidth: 350, mx: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ color: '#8e24aa', mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Total Revenue (Today)
                </Typography>
              </Box>

              <Typography variant="h4" sx={{ color: '#8e24aa' }}>
                ₹{todayRevenue.toFixed(2)}
              </Typography>
            </CardContent>

            <Button
              size="small"
              onClick={() => handlePayments("today")}
              sx={{ m: 2, color: '#8e24aa', fontWeight: 'medium' }}
            >
              View Details
            </Button>
          </DashboardCard>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} md={4}>
          <ChartCard>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', color: 'text.primary' }}>
              Order Status Distribution
            </Typography>
            {statusData.length > 0 ? (
              <ChartContainer>
                <Pie
                  data={pieChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { color: '#424242' },
                      },
                    },
                  }}
                />
              </ChartContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No status data available
              </Typography>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', color: 'text.primary' }}>
              Daily Revenue (Last 30 Days)
            </Typography>
            {revenueData.length > 0 ? (
              <ChartContainer>
                <Bar
                  data={barChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { color: '#424242' },
                      },
                    },
                    scales: {
                      x: {
                        ticks: {
                          color: '#424242',
                          maxRotation: 45,
                          minRotation: 45,
                          maxTicksLimit: 10,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: { color: '#424242' },
                      },
                    },
                  }}
                />
              </ChartContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No revenue data available
              </Typography>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', color: 'text.primary' }}>
              Table Status
            </Typography>
            {tableData.occupied + tableData.unoccupied > 0 ? (
              <ChartContainer>
                <Pie
                  data={pieTableData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { color: '#424242' },
                      },
                    },
                  }}
                />
              </ChartContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No table data available
              </Typography>
            )}
          </ChartCard>
        </Grid>
      </Grid>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2, mx: 4 }}>
        Recent Orders
      </Typography>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mx: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <FormControl sx={{ width: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Open">Open</MenuItem>
<MenuItem value="Paid">Paid</MenuItem>
<MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="outlined"
          onClick={() => setShowAllOrders(!showAllOrders)}
        >
          {showAllOrders ? 'Show Less' : 'Show More'}
        </Button>
      </Box>

      <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 6, mx: 4 }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Table ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Order ID</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#fff' }}>Amount (₹)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#fff' }}>GST</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#fff' }}>Total (₹)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentOrders.length > 0 ? (
                recentOrders.map((order, index) => (
                  <TableRow
                    key={order.id}
                    sx={{
                      bgcolor: index % 2 === 0 ? '#fafafa' : '#fff',
                      '&:hover': { bgcolor: '#f5f5f5', cursor: 'pointer' },
                    }}
                    onClick={() => handleOrderClick(order)}
                  >
                    <TableCell>{order.tableId || 'N/A'}</TableCell>
                    <TableCell>{order.id}</TableCell>
                    {/*<TableCell align="right">{order.baseSubtotal?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell align="right">{((order.cgstTotal || 0) + (order.sgstTotal || 0)).toFixed(2)}</TableCell>
                    <TableCell align="right">{order.grandTotal?.toFixed(2) || '0.00'}</TableCell>*/}
                    <TableCell align="right">
  {(
    order.orderStatus === "Cancelled"
      ? (order.items || []).reduce(
          (sum, item) => sum + (Number(item.totalPrice) || 0),
          0
        )
      : (order.baseSubtotal || 0)
  ).toFixed(2)}
</TableCell>

<TableCell align="right">
  {(
    order.orderStatus === "Cancelled"
      ? (
          ((order.items || []).reduce(
            (sum, item) => sum + (Number(item.cgstAmount) || 0),
            0
          )) +
          ((order.items || []).reduce(
            (sum, item) => sum + (Number(item.sgstAmount) || 0),
            0
          ))
        )
      : ((order.cgstTotal || 0) + (order.sgstTotal || 0))
  ).toFixed(2)}
</TableCell>

<TableCell align="right">
  {(
    order.orderStatus === "Cancelled"
      ? (
          (order.items || []).reduce(
            (sum, item) =>
              sum +
              (Number(item.totalPrice) || 0) +
              (Number(item.cgstAmount) || 0) +
              (Number(item.sgstAmount) || 0),
            0
          )
        )
      : (order.grandTotal || 0)
  ).toFixed(2)}
</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          order.status === 'Delivered' ? 'success.main' :
                            order.status === 'Ready' ? 'info.main' :
                              order.status === 'Preparing' ? 'warning.main' :
                                'text.primary'
                        }
                      >
                        {order.orderStatus || 'Open'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {order.createdAt?.toDate().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }) || 'N/A'}
                    </TableCell>
                    <TableCell>
  {order.createdAt?.toDate()
    ? dayjs(order.createdAt.toDate()).format('hh:mm A')
    : 'N/A'}
</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrintBill(order);
                        }}
                      >
                        Print Bill
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No orders available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
          <Box
  sx={{
    display: 'flex',
    justifyContent: 'flex-end',
    p: 2,
    borderTop: '2px solid #eee',
    bgcolor: '#fafafa'
  }}
>
  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
    Grand Total: ₹ {totalGrandTotal.toFixed(2)}
  </Typography>
</Box>
      </Card>

      {selectedOrder && (
        <Dialog open={!!selectedOrder} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Order Details - {selectedOrder.id}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Table ID:</strong> {selectedOrder.tableId || 'N/A'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Order ID:</strong> {selectedOrder.id}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Unit Code:</strong> {selectedOrder.unitCode || 'N/A'}
            </Typography>
            {/*<Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Total:</strong> ₹{selectedOrder.totalAmount?.toFixed(2) || '0.00'}
            </Typography>*/}
           {/*} <Typography variant="body1" sx={{ mb: 1 }}>
  <strong>Total Amount:</strong> ₹ {selectedOrder.baseSubtotal?.toFixed(2) || '0.00'}
</Typography>

<Typography variant="body1" sx={{ mb: 1 }}>
  <strong>CGST:</strong> ₹ {(selectedOrder.cgstTotal || 0).toFixed(2)}
</Typography>

<Typography variant="body1" sx={{ mb: 1 }}>
  <strong>SGST:</strong> ₹ {(selectedOrder.sgstTotal || 0).toFixed(2)}
</Typography>

<Typography variant="body1" sx={{ mb: 1 }}>
  <strong>Grand Total:</strong> ₹ {selectedOrder.grandTotal?.toFixed(2) || '0.00'}
</Typography>*/}
<Typography variant="body1" sx={{ mb: 1 }}>
  <strong>Total Amount:</strong> ₹ {
    (
      selectedOrder.orderStatus === "Cancelled"
        ? (selectedOrder.items || []).reduce(
            (sum, item) =>
              sum + (Number(item.totalPrice) || 0),
            0
          )
        : (selectedOrder.baseSubtotal || 0)
    ).toFixed(2)
  }
</Typography>

<Typography variant="body1" sx={{ mb: 1 }}>
  <strong>CGST:</strong> ₹ {
    (
      selectedOrder.orderStatus === "Cancelled"
        ? (selectedOrder.items || []).reduce(
            (sum, item) =>
              sum + (Number(item.cgstAmount) || 0),
            0
          )
        : (selectedOrder.cgstTotal || 0)
    ).toFixed(2)
  }
</Typography>

<Typography variant="body1" sx={{ mb: 1 }}>
  <strong>SGST:</strong> ₹ {
    (
      selectedOrder.orderStatus === "Cancelled"
        ? (selectedOrder.items || []).reduce(
            (sum, item) =>
              sum + (Number(item.sgstAmount) || 0),
            0
          )
        : (selectedOrder.sgstTotal || 0)
    ).toFixed(2)
  }
</Typography>

<Typography variant="body1" sx={{ mb: 1 }}>
  <strong>Grand Total:</strong> ₹ {
    (
      selectedOrder.orderStatus === "Cancelled"
        ? (selectedOrder.items || []).reduce(
            (sum, item) =>
              sum +
              (Number(item.totalPrice) || 0) +
              (Number(item.cgstAmount) || 0) +
              (Number(item.sgstAmount) || 0),
            0
          )
        : (selectedOrder.grandTotal || 0)
    ).toFixed(2)
  }
</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {/*<strong>Status:</strong> {selectedOrder.status || 'Confirmed'}*/}
              <strong>Status:</strong> {selectedOrder.orderStatus || 'Open'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Date:</strong>{' '}
              {selectedOrder.createdAt?.toDate().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }) || 'N/A'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {/*<strong>Payment Status:</strong> {selectedOrder.paymentStatus || 'Unknown'}*/}
              {selectedOrder.paymentStatus === 'Paid' && (
  <Typography variant="body1" sx={{ mb: 1 }}>
    <strong>Payment Status:</strong> {selectedOrder.paymentStatus || 'Unknown'}
  </Typography>
)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Payment Method:</strong> {selectedOrder.paymentMethod || 'N/A'}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Items Ordered:
            </Typography>
            <List>
              {selectedOrder.items?.map((item, index) => (
                <ListItem key={item.itemInstanceId || index}>
                  <ListItemText
                    primary={`${item.name || 'N/A'} (x${item.quantity || 1})`}
                    secondary={
                      <>
                        Price: ₹{item.price?.toFixed(2) || '0.00'} | Total: ₹{item.totalPrice?.toFixed(2) || '0.00'}
                        <br />
                        Instructions: {item.cookingInstructions || 'None'}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handlePrintBill(selectedOrder)}
              sx={{ mt: 2 }}
            >
              Print Bill
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default Home;